'use client';

import { ArrowRight, FlaskConical, Languages } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { cn } from '@kit/ui/utils';

interface EnglishUnavailableCardProps {
  /** IDs of the currently selected drugs (used for the Polish deep link) */
  drugIds: string[];
  /** Loads the example set and runs the (static) example analysis */
  onLoadExample?: () => void;
}

/**
 * Shown on the English demo when the selected set is not a subset of the
 * example drugs. No interaction check runs in that case; the card offers the
 * working example as the primary action and a plain link (full reload, so the
 * server re-resolves the language) to the Polish version with the same drugs.
 */
export function EnglishUnavailableCard({
  drugIds,
  onLoadExample,
}: EnglishUnavailableCardProps) {
  const { t } = useTranslation('interactions');
  const polishUrl = `/?leki=${drugIds.join(',')}`;

  return (
    <div className="rounded-xl border-2 border-amber-200 bg-gradient-to-br from-amber-50 to-stone-50 p-5 @[640px]:p-8 dark:border-amber-800/60 dark:from-amber-950/30 dark:to-stone-950/30">
      <div className="flex flex-col items-center text-center">
        <div className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 @[640px]:h-16 @[640px]:w-16 dark:bg-amber-900/50">
          <Languages className="h-7 w-7 text-amber-600 @[640px]:h-9 @[640px]:w-9 dark:text-amber-400" />
        </div>
        <h2 className="mt-3 text-lg font-bold text-amber-900 @[640px]:mt-4 @[640px]:text-xl dark:text-amber-100">
          {t('demo.title')}
        </h2>
        <p className="mt-2 max-w-lg text-sm text-amber-800/80 dark:text-amber-200/80">
          {t('demo.description')}
        </p>

        <div className="mt-6 flex w-full max-w-md flex-col items-center gap-3">
          {onLoadExample && (
            <button
              type="button"
              onClick={onLoadExample}
              data-test="demo-try-example"
              className={cn(
                'inline-flex w-full items-center justify-center gap-2 rounded-lg border-2 px-5 py-3 text-base font-semibold text-white',
                'transition-all hover:translate-x-[2px] hover:translate-y-[2px]',
                'border-green-600 bg-green-700 hover:bg-green-800',
                'shadow-[3px_3px_0_0_#16a34a] hover:shadow-[1px_1px_0_0_#16a34a]',
                'focus:ring-2 focus:ring-green-500 focus:ring-offset-2 focus:outline-none',
                'dark:border-green-400 dark:shadow-[3px_3px_0_0_#22c55e] dark:hover:shadow-[1px_1px_0_0_#22c55e]',
              )}
            >
              <FlaskConical className="h-5 w-5" />
              <span>{t('demo.tryExample')}</span>
            </button>
          )}

          <a
            href={polishUrl}
            data-test="demo-check-in-polish"
            className="group inline-flex items-center gap-1.5 text-sm font-medium text-stone-600 underline decoration-stone-300 underline-offset-4 transition-colors hover:text-stone-900 hover:decoration-stone-500 dark:text-stone-300 dark:decoration-stone-600 dark:hover:text-white"
          >
            <span>{t('demo.checkInPolish')}</span>
            <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
          </a>
        </div>
      </div>
    </div>
  );
}
